import React from 'react';
import PropTypes from 'prop-types';

import PrismGridHeader from './GridElements/PrismGridHeader';
import PrismGridBody from './GridElements/PrismGridBody';
import PrismPaiger from '../paiger/prismPaiger';
import initPrismGridControls from './GridElements/PrismGridControls';

import { COLUMN_PROP_TYPES, SORT_DIRECTION_PROP_TYPES } from './constants';
import { SORT_DIRECTIONS } from '../../../constants';

import propUtils from './utils/propUtils';

const initComponent = (flex = window.flex) => {

    const PrismGridControls = initPrismGridControls(flex);

    class PrismGrid extends React.Component {

        static propTypes = {
            data: PropTypes.arrayOf(PropTypes.object),
            columns: PropTypes.arrayOf(COLUMN_PROP_TYPES).isRequired,
            currentPage: propUtils.requiredIf(PropTypes.number, (props) => !props.hidePaiger),
            pageSize: propUtils.requiredIf(PropTypes.number, (props) => !props.hidePageOptions),
            totalRecords: propUtils.requiredIf(PropTypes.number, (props) => !props.hidePaiger),
            perPageOptions: PropTypes.arrayOf(PropTypes.number),
            sortName: PropTypes.string,
            sortOrder: SORT_DIRECTION_PROP_TYPES,
            paigerLabel: PropTypes.string,
            onChangePage: propUtils.requiredIf(PropTypes.func, (props) => !props.hidePaiger),
            onChangePageSize: propUtils.requiredIf(PropTypes.func, (props) => !props.hidePageOptions),
            onChangeSelection: PropTypes.func,
            onSort: PropTypes.func,
            headerClassName: PropTypes.string,
            bodyClassName: PropTypes.string,
            rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
            hideSelectAll: PropTypes.bool,
            hidePaiger: PropTypes.bool,
            hidePageOptions: PropTypes.bool
        };

        static defaultProps = {
            data: [],
            perPageOptions: [10, 25, 50],
            sortOrder: SORT_DIRECTIONS.ASC,
            onChangePageSize: () => { },
            onSelectionChange: () => { },
            onSort: () => { },
            rowClassName: ''
        };

        state = {
            selectedRows: [],
            selectAll: false
        };

        constructor(props) {
            super(props);

            this._grid = React.createRef();
        }

        initScrollToTop = () => {
            if (this._grid) {
                window.scrollTo(0, this._grid.current);
            }
        }

        handleSort = (field, sortDirection) => {
            this.initScrollToTop();

            this.props.onSort(field, sortDirection);
        }

        handleChangePage = ({ index }) => {
            this.initScrollToTop();

            this.props.onChangePage(index);
        }

        handleChangePageSize = (pageSize) => {
            this.initScrollToTop();

            this.props.onChangePageSize(pageSize);
        }

        handleChangeSelection = (data) => {
            const { state, props } = this;
            const index = state.selectedRows.findIndex((item) => item.id === data.id);

            if (index > -1) {
                state.selectedRows.splice(index, 1)
            } else {
                state.selectedRows.push(data);
            }

            props.onChangeSelection(data);
            this.setState({
                selectedRows: state.selectedRows,
                selectAll: false
            });
        }

        handleSelectAllRows = () => {
            const { state, props: { data } } = this;

            for (var i = 0; i < data.length; i++) {
                const item = data[i];
                const isSelected = state.selectedRows.indexOf(item) > -1;

                if ((state.selectAll && isSelected)
                    || (!state.selectAll && !isSelected)) {
                    this.handleSelectionChange(item);
                }
            }

            this.setState({ selectAll: !state.selectAll });
        }

        renderGridControls = ({
            columns,
            hidePageOptions,
            pageSize,
            perPageOptions,
            sortName,
            sortOrder,
            paigerLabel
        }) => {
            const sortableColumns = columns.filter(column => column.isSortable)
                .map(column => ({
                    field: column.dataField,
                    name: column.name
                }));

            return (
                <PrismGridControls
                    hidePageOptions={hidePageOptions}
                    pageSize={pageSize}
                    perPageOptions={perPageOptions}
                    sortableColumns={sortableColumns}
                    sortBy={sortName}
                    sortDirection={sortOrder}
                    paigerLabel={paigerLabel}
                    onSortSelect={this.handleSort}
                    onChangePageSize={this.handleChangePageSize}
                />
            );
        }

        renderGridHeader = ({
            columns,
            headerClassName,
            selectAll,
            hideSelectAll
        }) => (
            <PrismGridHeader
                columns={columns}
                headerClassName={headerClassName}
                selectAll={selectAll}
                hideSelectAll={hideSelectAll}
                onSelectAllRows={this.handleSelectAllRows}
            />
        )

        renderGridBody = ({
            data,
            columns,
            bodyClassName,
            rowClassName,
            selectedRows
        }) => (
            <PrismGridBody
                data={data}
                columns={columns}
                className={bodyClassName}
                rowClassName={rowClassName}
                selection={selectedRows}
                onChangeSelection={this.handleChangeSelection}
            />
        )

        renderPaiger = ({ currentPage, pageSize, totalRecords }) => {
            const totalPages = Math.ceil(totalRecords / pageSize);

            return (
                <div className="prism-grid__paiger">
                    <PrismPaiger
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={this.handleChangePage}
                    />
                </div>
            );
        }

        render() {
            return (
                <div ref={this._grid} className="prism-sandbox">
                    <div className="prism-grid">
                        {this.renderGridControls({ ...this.props })}
                        {this.renderGridHeader({ ...this.props, selectAll: this.state.selectAll })}
                        {this.renderGridBody({ ...this.props, selectedRows: this.state.selectedRows })}
                        {!this.props.hidePaiger && this.renderPaiger(this.props)}
                    </div>
                </div>
            );
        }
    }

    return PrismGrid
}

export default initComponent;
